/**
 * 영업건 검색·선택 모달 (재사용 가능).
 *
 * 발주서 작성 폼의 [관련 영업건] 검색에서 사용.
 * 통합 검색 q: 매출번호 / 사업명(프로젝트명) / 거래처(제안업체/계산서발행) / 엔드유저 부분일치.
 *
 * Props:
 *   open / onClose / onPick(row) — 클릭 시 호출
 *   defaultCompanyId — 모달 열 때 미리 필터 (선택)
 *   excludeStages — 검색 결과에서 제외할 status (예: ['발행','판보'] — 견적서/발주서 연동에서 제외)
 */

import { useCallback, useEffect, useState } from 'react';
import { listSales } from '../lib/sales-api';
import type { CompanyId, SalesListRow, SalesStatus } from '../lib/types';

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (row: SalesListRow) => void;
  defaultCompanyId?: CompanyId;
  excludeStages?: SalesStatus[];
  title?: string;
  /** 모달 열 때 자동 채워질 검색어 (열기 직전에 호출자가 가지고 있는 매출번호 등) */
  initialQuery?: string;
}

const ALL_STAGES: SalesStatus[] = ['일반', '유력', '발주', '납품', '발행', '판보'];

export function SalesPickerModal({
  open,
  onClose,
  onPick,
  defaultCompanyId,
  excludeStages,
  title,
  initialQuery,
}: Props) {
  const [query, setQuery] = useState('');
  const [companyId, setCompanyId] = useState<'' | CompanyId>(defaultCompanyId ?? '');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SalesListRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery ?? '');
    setCompanyId(defaultCompanyId ?? '');
    setResults([]);
    setErr(null);
  }, [open, defaultCompanyId, initialQuery]);

  const runSearch = useCallback(async (q?: string) => {
    setSearching(true);
    setErr(null);
    try {
      const allowedStages = excludeStages
        ? ALL_STAGES.filter((s) => !excludeStages.includes(s))
        : undefined;
      const queryStr = (q ?? query).trim();
      const r = await listSales({
        q: queryStr || undefined,
        companyId: companyId || undefined,
        stage: allowedStages,
        pageSize: 50,
      });
      setResults(r.data);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSearching(false);
    }
  }, [query, companyId, excludeStages]);

  // 모달 열릴 때 자동 검색 — initialQuery 가 있으면 그 값으로 즉시 필터링
  useEffect(() => {
    if (open) runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-start justify-center overflow-y-auto p-4"
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-3xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="ui-modal-title">{title ?? '영업건 검색'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>

        <div className="p-5">
          <div className="text-xs text-slate-500 mb-2">
            매출번호 / 사업명(프로젝트명) / 거래처(제안업체·계산서발행) / 엔드유저 등으로 검색
          </div>
          <div className="flex gap-2 mb-2">
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value as '' | CompanyId)}
              className="ui-input"
            >
              <option value="">전체 사업자</option>
              <option value="SM">SM</option>
              <option value="EJ">EJ</option>
            </select>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runSearch();
                }
              }}
              placeholder="검색어 입력 후 Enter (예: 강남구, 서경산업, 한국전력 ...)"
              className="ui-input flex-1"
              autoFocus
            />
            <button
              type="button"
              onClick={() => runSearch()}
              disabled={searching}
              className="btn btn-primary"
            >
              {searching ? '검색중...' : '검색'}
            </button>
          </div>

          {err && (
            <div className="mb-2 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
              {err}
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded">
            {results.length === 0 && !searching ? (
              <div className="text-center text-slate-400 py-8 text-sm">
                검색 결과 없음
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">매출번호</th>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">사업자</th>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">진행</th>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">제안업체</th>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">엔드유저</th>
                    <th className="px-2 py-1.5 text-left font-medium text-slate-600">프로젝트명</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr
                      key={r.salesId}
                      onClick={() => {
                        onPick(r);
                        onClose();
                      }}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="px-2 py-1.5 font-mono text-xs">{r.salesId}</td>
                      <td className="px-2 py-1.5">
                        <span
                          className={
                            'inline-block px-1.5 py-0.5 text-[11px] rounded font-medium ' +
                            (r.companyId === 'SM'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700')
                          }
                        >
                          {r.companyId}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{r.status}</td>
                      <td className="px-2 py-1.5">{r.proposalCompany ?? '-'}</td>
                      <td className="px-2 py-1.5">{r.endUser ?? '-'}</td>
                      <td className="px-2 py-1.5 max-w-[24ch] truncate" title={r.projectName ?? ''}>
                        {r.projectName ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}
